import React, { useState, useRef, useEffect, useCallback } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X } from 'lucide-react';

interface InlineEditTagsProps {
  tags: string[];
  onSave: (tags: string[]) => void;
  savedTags?: string[];
}

const InlineEditTagsComponent: React.FC<InlineEditTagsProps> = ({
  tags: initialTags,
  onSave,
  savedTags = []
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editStartTime = useRef<number>(0);

  const handleSave = useCallback(() => {
    setIsEditing(false);
    if (JSON.stringify(tags) !== JSON.stringify(initialTags)) {
      onSave(tags);
    }
  }, [tags, initialTags, onSave]);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Ignore clicks that happen within 300ms of opening
      if (Date.now() - editStartTime.current < 300) {
        return;
      }

      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        handleSave();
      }
    };

    // Add listener immediately but with timestamp check
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isEditing, handleSave]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editStartTime.current = Date.now();
    setIsEditing(true);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setTags(initialTags);
    }
  };

  if (!isEditing) {
    return (
      <div
        style={(styles as any).inlineEdit?.tagsContainer || {}}
        onDoubleClick={handleDoubleClick}
        title="Double-click to edit"
      >
        {tags.length > 0 ? (
          tags.slice(0, 3).map((tag, index) => (
            <span key={index} style={styles.table.tagPill}>
              {tag}
            </span>
          ))
        ) : (
          <span style={(styles as any).inlineEdit?.placeholder || {}}>No tags</span>
        )}
        {tags.length > 3 && (
          <span style={styles.table.tagCounter}>
            +{tags.length - 3}
          </span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={(styles as any).inlineEdit?.editContainer || {}}>
      <div style={(styles as any).inlineEdit?.tagsEditWrapper || {}}>
        {tags.map((tag, index) => (
          <span key={index} style={(styles as any).inlineEdit?.tagPillEdit || {}}>
            {tag}
            <button
              onClick={() => handleRemoveTag(index)}
              style={(styles as any).inlineEdit?.tagRemoveButton || {}}
              onMouseDown={(e) => e.preventDefault()}  // Prevent focus loss
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={tagInput}
          onChange={(e) => {
            setTagInput(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyPress={handleKeyPress}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          style={(styles as any).inlineEdit?.tagInput || {}}
          placeholder="Add tag..."
        />
      </div>
      {showSuggestions && savedTags.length > 0 && tagInput && (
        <div style={(styles as any).inlineEdit?.suggestions || {}}>
          {savedTags
            .filter(tag =>
              !tags.includes(tag) &&
              tag.toLowerCase().includes(tagInput.toLowerCase())
            )
            .slice(0, 5)
            .map((tag, index) => (
              <button
                key={index}
                onMouseDown={(e) => {
                  e.preventDefault();  // Prevent focus loss
                  setTags([...tags, tag]);
                  setTagInput('');
                  setShowSuggestions(false);
                }}
                style={(styles as any).inlineEdit?.suggestionItem || {}}
              >
                {tag}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export const InlineEditTags = React.memo(InlineEditTagsComponent);